import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Plus, Trash2, Calendar, MapPin, Target, Loader2, CreditCard, Users, AlertCircle, CheckCircle, UserPlus, Mail, Search, Link as LinkIcon, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [feeId, setFeeId] = useState(location.state?.fee_id || null);
  const [draftId, setDraftId] = useState(location.state?.draft_id || null);
  const [isAutoApproved, setIsAutoApproved] = useState(location.state?.isAutoApproved || false);
  const [planInfo, setPlanInfo] = useState(null);
  const [totalBudget, setTotalBudget] = useState(0);
  const [budgetRemaining, setBudgetRemaining] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    blessings: '',
    invited_by: '',
    event_code_prefix: '',
    date: '',
    time: '12:00',
    location: '',
    expected_guests: '',
    event_photos: [],
    upi_ids: []
  });
  
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [contributionGoals, setContributionGoals] = useState([
    { title: '', description: '', target_amount: '', additional_info: '', minimum_contribution: '', wishlist_item_id: null }
  ]);

  const [newUpiId, setNewUpiId] = useState('');

  // Wishlist state
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [currentGoalIndex, setCurrentGoalIndex] = useState(null);
  const [wishlists, setWishlists] = useState([]);  // New: Array of wishlists with items
  const [selectedWishlistId, setSelectedWishlistId] = useState(null);
  const [wishlistCategories, setWishlistCategories] = useState([]);
  const [showAddWishlistItem, setShowAddWishlistItem] = useState(false);
  const [newWishlistItem, setNewWishlistItem] = useState({
    name: '',
    category_id: null,
    price: '',
    link: '',
    description: ''
  });

  // Co-host management state
  const [coHosts, setCoHosts] = useState([]);
  const [coHostMethod, setCoHostMethod] = useState('email');
  const [coHostEmail, setCoHostEmail] = useState('');
  const [coHostName, setCoHostName] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [showCouponBanner, setShowCouponBanner] = useState(false);
  const [activeCoupon, setActiveCoupon] = useState(null);

  // Check for coupon in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const couponCode = params.get('coupon');
    
    if (couponCode && !feeId) {
      validateAndUseCoupon(couponCode);
    }
  }, [location.search]);

  const validateAndUseCoupon = async (code) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/use-plan-coupon`, { code }, { withCredentials: true });
      
      if (response.data.success) {
        setFeeId(response.data.fee_id);
        setActiveCoupon({
          code: code,
          type: response.data.coupon_type,
          plan_type: response.data.plan_type
        });
        setShowCouponBanner(true);
        toast.success(response.data.message || 'Coupon applied!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon');
      navigate('/select-plan');
    } finally {
      setLoading(false);
    }
  };

  // Check if user came from plan selection with fee_id or from draft
  useEffect(() => {
    // If loading from draft, load it first (it contains fee_id)
    if (draftId && !feeId) {
      loadDraft();
      return;
    }
    
    // Skip redirect if we're waiting for coupon validation
    const params = new URLSearchParams(location.search);
    if (params.get('coupon') && !feeId) {
      return; // Wait for coupon validation
    }
    
    if (!feeId) {
      toast.error('Please select a plan first');
      navigate('/select-plan');
      return;
    }
    
    fetchPlanInfo();
  }, [feeId, navigate, location.search]);

  // Load draft when draft_id is provided and fee_id is set
  useEffect(() => {
    if (draftId && feeId) {
      // Only load if we haven't loaded yet
      if (!formData.title && !formData.description) {
        loadDraft();
      }
    }
  }, [draftId, feeId]);

  const loadDraft = async () => {
    try {
      const response = await axios.get(`${API}/events/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.draft) {
        const draft = response.data.draft;
        
        // Set fee_id from draft if not already set
        if (draft.fee_id && !feeId) {
          setFeeId(draft.fee_id);
        }
        
        // Load form data
        setFormData({
          title: draft.title || '',
          description: draft.description || '',
          blessings: draft.blessings || '',
          invited_by: draft.invited_by || '',
          event_code_prefix: draft.event_code_prefix || '',
          date: draft.date || '',
          time: draft.time || '12:00',
          location: draft.location || '',
          expected_guests: draft.expected_guests || '',
          event_photos: draft.event_photos || [],
          upi_ids: draft.upi_ids || []
        });

        // Load goals
        if (draft.contribution_goals && draft.contribution_goals.length > 0) {
          setContributionGoals(draft.contribution_goals);
        }

        // Load co-hosts
        if (draft.co_hosts && draft.co_hosts.length > 0) {
          setCoHosts(draft.co_hosts);
        }

        toast.success('Draft loaded successfully!');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Failed to load draft');
      navigate('/select-plan');
    }
  };

  // Fetch plan information
  const fetchPlanInfo = async () => {
    try {
      const response = await axios.get(`${API}/pricing/plans`);
      const allPlans = response.data.plans;
      
      // Get fee details to know which plan was selected
      const feeResponse = await axios.get(`${API}/events/my-pending-fees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const myFee = feeResponse.data.find(f => f.id === feeId);
      if (myFee) {
        const selectedPlan = allPlans.find(p => p.plan_type === myFee.plan_type);
        setPlanInfo(selectedPlan);
        setBudgetRemaining(selectedPlan.max_budget);
      }
    } catch (error) {
      console.error('Failed to fetch plan info:', error);
    }
  };

  // Calculate total budget when goals change
  useEffect(() => {
    const total = contributionGoals.reduce((sum, goal) => {
      return sum + (parseFloat(goal.target_amount) || 0);
    }, 0);
    setTotalBudget(total);
    if (planInfo) {
      setBudgetRemaining(planInfo.max_budget - total);
    }
  }, [contributionGoals, planInfo]);

  // Check for copied event data on mount
  useEffect(() => {
    const copiedData = sessionStorage.getItem('copyEventData');
    if (copiedData) {
      try {
        const eventData = JSON.parse(copiedData);
        setFormData({
          title: eventData.title || '',
          description: eventData.description || '',
          blessings: eventData.blessings || '',
          invited_by: eventData.invited_by || '',
          event_code_prefix: eventData.event_code_prefix || '',
          date: eventData.date || '',
          time: eventData.time || '12:00',
          location: eventData.location || '',
          expected_guests: eventData.expected_guests || '',
          event_photos: eventData.event_photos || [],
          upi_ids: eventData.upi_ids || []
        });
        
        if (eventData.contribution_goals && eventData.contribution_goals.length > 0) {
          setContributionGoals(eventData.contribution_goals);
        }
        
        // Clear from sessionStorage after loading
        sessionStorage.removeItem('copyEventData');
        
        toast.success('Event data loaded! Edit as needed before creating.');
      } catch (error) {
        console.error('Failed to load copied event data:', error);
      }
    }
  }, []);

  const handleAddGoal = () => {
    if (contributionGoals.length >= 5) {
      toast.error('Maximum 5 goals allowed (including Others option)');
      return;
    }
    setContributionGoals([...contributionGoals, { title: '', description: '', target_amount: '', additional_info: '', minimum_contribution: '', wishlist_item_id: null }]);
  };

  const handleRemoveGoal = (index) => {
    if (contributionGoals.length > 1) {
      setContributionGoals(contributionGoals.filter((_, i) => i !== index));
    }
  };

  const handleGoalChange = (index, field, value) => {
    const updated = [...contributionGoals];
    updated[index][field] = value;
    setContributionGoals(updated);
  };

  // Wishlist handlers
  const fetchWishlists = async () => {
    try {
      const response = await axios.get(`${API}/wishlists/for-event-selection`, { withCredentials: true });
      const fetchedWishlists = response.data.wishlists || [];
      setWishlists(fetchedWishlists);
      // Auto-select first wishlist if available
      if (fetchedWishlists.length > 0 && !selectedWishlistId) {
        setSelectedWishlistId(fetchedWishlists[0].id);
      }
    } catch (error) {
      console.error('Error fetching wishlists:', error);
    }
  };

  const fetchWishlistCategories = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/categories`, { withCredentials: true });
      setWishlistCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const openWishlistModal = (goalIndex) => {
    setCurrentGoalIndex(goalIndex);
    setShowWishlistModal(true);
    fetchWishlists();
    fetchWishlistCategories();
  };

  // Get items from selected wishlist
  const getSelectedWishlistItems = () => {
    const selected = wishlists.find(w => w.id === selectedWishlistId);
    return selected?.items || [];
  };

  const linkWishlistItem = (item) => {
    if (currentGoalIndex !== null) {
      const updated = [...contributionGoals];
      updated[currentGoalIndex] = {
        ...updated[currentGoalIndex],
        title: item.name,
        description: item.description || updated[currentGoalIndex].description,
        target_amount: item.price || updated[currentGoalIndex].target_amount,
        additional_info: item.link || updated[currentGoalIndex].additional_info,
        wishlist_item_id: item.id
      };
      setContributionGoals(updated);
      setShowWishlistModal(false);
      setCurrentGoalIndex(null);
      toast.success('Wishlist item linked to goal!');
    }
  };

  const handleAddWishlistItem = async () => {
    if (!newWishlistItem.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }

    if (!selectedWishlistId) {
      toast.error('Please select a wishlist first');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/wishlists/${selectedWishlistId}/items`,
        {
          name: newWishlistItem.name,
          category_id: newWishlistItem.category_id || null,
          price: newWishlistItem.price ? parseFloat(newWishlistItem.price) : null,
          link: newWishlistItem.link || null,
          description: newWishlistItem.description || null
        },
        { withCredentials: true }
      );

      toast.success('Item added to wishlist!');
      setNewWishlistItem({ name: '', category_id: null, price: '', link: '', description: '' });
      setShowAddWishlistItem(false);
      fetchWishlists();  // Refresh to get the new item
    } catch (error) {
      console.error('Error adding wishlist item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleAddUPI = () => {
    if (!newUpiId.trim()) {
      toast.error('Please enter a UPI ID');
      return;
    }

    if (formData.upi_ids.includes(newUpiId.trim())) {
      toast.error('This UPI ID is already added');
      return;
    }

    setFormData({
      ...formData,
      upi_ids: [...formData.upi_ids, newUpiId.trim()]
    });
    setNewUpiId('');
  };

  const handleRemoveUPI = (upiId) => {
    setFormData({
      ...formData,
      upi_ids: formData.upi_ids.filter(id => id !== upiId)
    });
  };



  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (formData.event_photos.length + files.length > 4) {
      toast.error('Maximum 4 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    
    for (const file of files) {
      if (formData.event_photos.length >= 4) break;
      
      try {
        const formDataToUpload = new FormData();
        formDataToUpload.append('file', file);

        const response = await axios.post(`${API}/upload-photo`, formDataToUpload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setFormData(prev => ({
          ...prev,
          event_photos: [...prev.event_photos, response.data.photo_url]
        }));
      } catch (error) {
        toast.error('Failed to upload photo');
        console.error(error);
      }
    }
    
    setUploadingPhoto(false);
    e.target.value = ''; // Reset input
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      event_photos: prev.event_photos.filter((_, i) => i !== index)
    }));
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    
    try {
      const draftData = {
        id: draftId,
        fee_id: feeId,
        ...formData,
        expected_guests: formData.expected_guests ? parseInt(formData.expected_guests) : null,
        contribution_goals: contributionGoals,
        co_hosts: coHosts
      };

      const response = await axios.post(
        `${API}/events/drafts`,
        draftData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        setDraftId(response.data.draft.id);
        toast.success('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Reset errors
    const errors = {};
    
    // Validation with error tracking
    if (!formData.title) errors.title = true;
    if (!formData.description) errors.description = true;
    if (!formData.invited_by) errors.invited_by = true;
    if (!formData.date) errors.date = true;
    if (!formData.location) errors.location = true;
    if (!formData.event_code_prefix) errors.event_code_prefix = true;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fill in all required fields (highlighted in red)');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validate UPI ID is mandatory
    if (!formData.upi_ids || formData.upi_ids.length === 0) {
      toast.error('At least one UPI ID is mandatory for receiving contributions');
      setFieldErrors({ upi_id: true });
      document.getElementById('upi_id')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Clear errors if validation passes
    setFieldErrors({});

    // Validate event code prefix (already auto-generated, should be exactly 6 characters)
    const prefix = formData.event_code_prefix.trim();
    if (prefix.length !== 6) {
      toast.error('Please fill in "Invited By" field to auto-generate event code prefix');
      return;
    }
    if (!/^[a-zA-Z0-9]+$/.test(prefix)) {
      toast.error('Event code prefix can only contain letters and numbers');
      return;
    }

    // Validate date and time
    const eventDateTime = new Date(`${formData.date}T${formData.time}`);
    const now = new Date();
    const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);

    // Check if event is at least 1 hour in the future
    if (hoursUntilEvent < 1) {
      toast.error('Event must be scheduled at least 1 hour from now');
      setFieldErrors({ date: true, time: true });
      return;
    }

    // Validate date is within 60 days
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 60);

    if (selectedDate < today) {
      toast.error('Event date cannot be in the past');
      return;
    }

    if (selectedDate > maxDate) {
      toast.error('Event date must be within the next 60 days');
      return;
    }

    const validGoals = contributionGoals.filter(
      (goal) => goal.title && goal.target_amount > 0
    );

    if (validGoals.length === 0) {
      toast.error('Please add at least one contribution goal');
      return;
    }

    // Prepare event data
    const eventData = {
      fee_id: feeId,
      ...formData,
      expected_guests: formData.expected_guests ? parseInt(formData.expected_guests) : null,
      contribution_goals: validGoals.map((goal) => ({
        ...goal,
        target_amount: parseFloat(goal.target_amount),
        minimum_contribution: goal.minimum_contribution ? parseFloat(goal.minimum_contribution) : null
      }))
    };

    setLoading(true);

    try {
      // Create event directly (payment already submitted in plan selection)
      const response = await axios.post(
        `${API}/events`,
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        const eventId = response.data.id;
        
        // Add co-hosts if any were specified
        if (coHosts.length > 0) {
          for (const coHost of coHosts) {
            try {
              await axios.post(
                `${API}/events/${eventId}/co-hosts`,
                {
                  method: coHost.method,
                  email: coHost.email,
                  name: coHost.name
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (error) {
              console.error('Failed to add co-host:', coHost.email, error);
              toast.error(`Failed to add co-host: ${coHost.email}`);
            }
          }
          if (coHosts.length > 0) {
            toast.success(`Event created with ${coHosts.length} co-host(s)!`);
          }
        } else {
          toast.success('Event created! Waiting for admin to verify your payment.', {
            description: 'You can view your event, but sharing will be enabled after verification.',
            duration: 6000
          });
        }
        
        // Delete draft if it exists
        if (draftId) {
          try {
            await axios.delete(`${API}/events/drafts/${draftId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (error) {
            console.error('Failed to delete draft:', error);
          }
        }
        
        navigate(`/event/${eventId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy from-opacity-5 via-contribo-gold via-opacity-5 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          
          {/* Coupon Banner */}
          {showCouponBanner && activeCoupon && (
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-lg mb-4 shadow-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 flex-shrink-0" />
                <div>
                  <p className="font-bold">
                    {activeCoupon.type === 'free' ? '🎁 Free Plan Activated!' : '✅ Plan Resume Coupon Applied!'}
                  </p>
                  <p className="text-sm text-purple-200">
                    Coupon: <span className="font-mono font-bold">{activeCoupon.code}</span> • 
                    Plan: <span className="capitalize">{activeCoupon.plan_type}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 rounded-lg">
            <h1 className="text-2xl font-bold text-white">Create New Event</h1>
            <p className="text-white mt-2 text-sm font-semibold">Set up your event with specific contribution goals</p>
            
            {/* Plan Details Banner */}
            {planInfo && (
              <div className="mt-3 bg-white/20 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-white">
                  <div className="flex items-center gap-2">
                    <span className="bg-white text-contribo-navy px-2 py-0.5 rounded text-xs font-bold">
                      {planInfo.name || planInfo.plan_type?.toUpperCase()}
                    </span>
                    <span className="text-sm">₹{planInfo.fee} Plan</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="opacity-80">Max Budget:</span>
                      <span className="font-bold">₹{planInfo.max_budget?.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="opacity-80">Max RSVPs:</span>
                      <span className="font-bold">{planInfo.max_rsvps}</span>
                    </span>
                  </div>
                </div>
                {budgetRemaining !== null && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-white/80 mb-1">
                      <span>Budget Used: ₹{totalBudget.toLocaleString()}</span>
                      <span>Remaining: ₹{budgetRemaining.toLocaleString()}</span>
                    </div>
                    <div className="bg-white/30 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full transition-all ${budgetRemaining < 0 ? 'bg-red-500' : 'bg-white'}`}
                        style={{ width: `${Math.min((totalBudget / planInfo.max_budget) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Payment Status Notice */}
        {isAutoApproved ? (
          <div className="mb-6 bg-green-50 border-2 border-green-400 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-900">🎉 Instant Approval - No Verification Needed!</h3>
              <p className="text-sm text-green-800 mt-1">
                Your event fee was waived with coupon. Your event will be instantly approved and you can share it immediately after creation!
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900">Payment Verification Pending</h3>
              <p className="text-sm text-yellow-800 mt-1">
                Your payment is under verification by Contribo admin. Your event will be created but the Event ID and 
                sharing options will be available only after admin approval.
              </p>
              <p className="text-sm text-yellow-800 mt-2">
                <strong>⏱️ Verification Time:</strong><br/>
                • 8 AM - 8 PM: 3-6 hours | • 8 PM - 8 AM: 6-10 hours
              </p>
            </div>
          </div>
        )}

        {/* Event Details */}
        <Card className="glass-card mb-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl flex items-center">
              <Calendar className="mr-2 h-6 w-6" />
              Event Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-blue-900 font-semibold text-base">Event Title *</Label>
              <Input
                id="title"
                placeholder="Mention the Event Title e.g., Gowri's Birthday Party or Lucky's Bachelor party or Balu Weds Swapna etc"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={fieldErrors.title ? 'border-2 border-red-500' : ''}
                data-testid="event-title-input"
              />
              {fieldErrors.title && <p className="text-xs text-red-600 mt-1">This field is required</p>}
            </div>

            <div>
              <Label htmlFor="description" className="text-blue-900 font-semibold text-base">Description *</Label>
              <Textarea
                id="description"
                placeholder="Tell guests about your event..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className={fieldErrors.description ? 'border-2 border-red-500' : ''}
                data-testid="event-description-input"
              />
              {fieldErrors.description && <p className="text-xs text-red-600 mt-1">This field is required</p>}
            </div>

            <div>
              <Label htmlFor="blessings" className="text-blue-900 font-semibold text-base">Blessings / Additional Message</Label>
              <Textarea
                id="blessings"
                placeholder="Add a special message or blessings for your guests..."
                value={formData.blessings || ''}
                onChange={(e) => setFormData({ ...formData, blessings: e.target.value })}
                rows={3}
                data-testid="event-blessings-input"
              />
              <p className="text-xs text-blue-700 mt-1">
                Share a heartfelt message or blessing with your guests (optional)
              </p>
            </div>

            <div>
              <Label htmlFor="invited_by" className="text-blue-900 font-semibold text-base">Invited By (Individual / Company / Family Name) *</Label>
              <Input
                id="invited_by"
                placeholder="e.g., Thiruveedula's Family"
                value={formData.invited_by || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  
                  // Auto-generate event code prefix from first 6 characters (letters and numbers only)
                  if (value) {
                    const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
                    setFormData({ ...formData, invited_by: value, event_code_prefix: cleanedValue });
                  } else {
                    // Clear both fields if invited_by is empty
                    setFormData({ ...formData, invited_by: '', event_code_prefix: '' });
                  }
                }}
                className={fieldErrors.invited_by ? 'border-2 border-red-500' : ''}
                data-testid="invited-by-input"
              />
              <p className="text-xs text-blue-700 mt-1">
                This will appear in the invitation message and auto-generate event code prefix (letters & numbers)
              </p>
              {fieldErrors.invited_by && <p className="text-xs text-red-600 mt-1">This field is required</p>}
            </div>

            <div>
              <Label htmlFor="event_code_prefix" className="text-blue-900 font-semibold text-base">Event Code Prefix * (Auto Generated – Don't Edit)</Label>
              <Input
                id="event_code_prefix"
                placeholder="First 6 characters (letters & numbers) from Invited By"
                value={formData.event_code_prefix || ''}
                disabled
                className="bg-gray-100 cursor-not-allowed"
                data-testid="event-code-prefix-input"
              />
              <p className="text-xs text-gray-700 mt-1 font-medium">
                Auto-generated from "Invited By" field (letters & numbers only). Final code: {formData.event_code_prefix || 'XXXXXX'}_NNNNNN
              </p>
              {fieldErrors.event_code_prefix && <p className="text-xs text-red-600 mt-1">Please fill in "Invited By" field to generate prefix</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date" className="text-blue-900 font-semibold text-base">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Event Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className={fieldErrors.date ? 'border-2 border-red-500' : ''}
                  data-testid="event-date-input"
                />
                <p className="text-xs text-blue-700 mt-1">Up to 60 days in advance</p>
                {fieldErrors.date && <p className="text-xs text-red-600 mt-1">This field is required</p>}
              </div>

              <div>
                <Label htmlFor="time" className="text-blue-900 font-semibold text-base">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Event Time *
                </Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  data-testid="event-time-input"
                />
                <p className="text-xs text-blue-700 mt-1">RSVP locks 12hrs before</p>
              </div>

              <div>
                <Label htmlFor="location" className="text-blue-900 font-semibold text-base">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Location *
                </Label>
                <Input
                  id="location"
                  placeholder="e.g., Central Park, NYC"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={fieldErrors.location ? 'border-2 border-red-500' : ''}
                  data-testid="event-location-input"
                />
                {fieldErrors.location && <p className="text-xs text-red-600 mt-1">This field is required</p>}
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="expected_guests" className="text-blue-900 font-semibold text-base">
                <Users className="inline h-4 w-4 mr-1" />
                Expected Number of Guests
              </Label>
              <Input
                id="expected_guests"
                type="number"
                placeholder="e.g., 50"
                value={formData.expected_guests}
                onChange={(e) => setFormData({ ...formData, expected_guests: e.target.value })}
                min="1"
                data-testid="expected-guests-input"
              />
              <p className="text-xs text-blue-700 mt-1">
                How many guests are you planning to invite? (optional)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contribution Goals */}
        <Card className="glass-card mb-6 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl flex items-center">
                  <Target className="inline h-6 w-6 mr-2" />
                  Contribution Goals
                </CardTitle>
                <p className="text-sm text-white text-opacity-90 mt-1">
                  Add items or expenses guests can contribute to
                </p>
                {planInfo && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Your Plan Budget Limit</p>
                        <p className="text-sm font-bold text-blue-900">₹{planInfo.max_budget.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Total Goals Budget</p>
                        <p className="text-sm font-bold text-purple-900">₹{totalBudget.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Budget Remaining</p>
                        <p className={`text-sm font-bold ${budgetRemaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ₹{budgetRemaining.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {budgetRemaining < 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        ⚠️ Your total goals budget exceeds the plan limit by ₹{Math.abs(budgetRemaining).toLocaleString()}. Please adjust.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <Button
                className="bg-white text-purple-600 hover:bg-purple-50"
                size="sm"
                onClick={handleAddGoal}
                data-testid="add-goal-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Goal
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {contributionGoals.map((goal, index) => (
              <div
                key={index}
                className="p-4 border-2 border-purple-300 rounded-lg space-y-3 bg-white shadow-md"
                data-testid={`goal-${index}`}
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-purple-700 text-lg">Goal {index + 1}</h4>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openWishlistModal(index)}
                      className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-none hover:from-purple-600 hover:to-purple-700"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Link from Wishlist
                    </Button>
                    {contributionGoals.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGoal(index)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-goal-${index}-btn`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-purple-900 font-semibold text-base">Goal Title (Mention Any item / Expenses / Event etc) *</Label>
                  <Input
                    placeholder="e.g., Venue Rental, Photography, Food, Liquor etc"
                    value={goal.title}
                    onChange={(e) => handleGoalChange(index, 'title', e.target.value)}
                    data-testid={`goal-${index}-title`}
                  />
                  {goal.wishlist_item_id && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      ✓ Linked to wishlist item
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-purple-900 font-semibold text-base">Description</Label>
                  <Textarea
                    placeholder="Details about this goal..."
                    value={goal.description}
                    onChange={(e) => handleGoalChange(index, 'description', e.target.value)}
                    rows={2}
                    data-testid={`goal-${index}-description`}
                  />
                </div>

                <div>
                  <Label className="text-purple-900 font-semibold text-base">Additional Info / Shopping URL</Label>
                  <Input
                    placeholder="e.g., https://amazon.in/product/xyz or any helpful information"
                    value={goal.additional_info}
                    onChange={(e) => handleGoalChange(index, 'additional_info', e.target.value)}
                    data-testid={`goal-${index}-info`}
                  />
                  <p className="text-xs text-purple-700 mt-1">
                    Add a shopping link or additional information to help contributors
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-purple-900 font-semibold text-base">Target Amount (₹) *</Label>
                    <Input
                      type="number"
                      placeholder="5000"
                      value={goal.target_amount}
                      onChange={(e) => handleGoalChange(index, 'target_amount', e.target.value)}
                      min="0"
                      data-testid={`goal-${index}-amount`}
                    />
                  </div>
                  <div>
                    <Label className="text-purple-900 font-semibold text-base">Minimum Contribution (₹)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={goal.minimum_contribution}
                      onChange={(e) => handleGoalChange(index, 'minimum_contribution', e.target.value)}
                      min="0"
                      data-testid={`goal-${index}-min`}
                    />
                    <p className="text-xs text-purple-700 mt-1">
                      Optional minimum amount
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* UPI Payment IDs */}
        <Card className="glass-card mb-6 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl flex items-center">
              <CreditCard className="inline h-6 w-6 mr-2" />
              UPI Payment IDs <span className="text-yellow-300">*</span>
            </CardTitle>
            <p className="text-sm text-white text-opacity-90 mt-1">
              <span className="font-semibold">Mandatory:</span> Add at least one UPI ID to receive contributions directly
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter UPI ID (e.g., user@paytm)"
                value={newUpiId}
                onChange={(e) => setNewUpiId(e.target.value)}
                data-testid="upi-input"
                className="text-gray-900"
              />
              <Button
                className="bg-green-600 text-white hover:bg-green-700"
                onClick={handleAddUPI}
                data-testid="add-upi-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {formData.upi_ids.length > 0 && (
              <div className="space-y-2">
                {formData.upi_ids.map((upiId, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-white rounded-lg border-2 border-green-300 shadow-md"
                    data-testid={`upi-item-${index}`}
                  >
                    <span className="font-medium text-green-700">{upiId}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveUPI(upiId)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`remove-upi-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


        {/* Event Photos */}
        <Card className="glass-card mb-6 bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl">
              📸 Event Photos (Optional)
            </CardTitle>
            <p className="text-sm text-white text-opacity-90">Upload up to 4 photos for your event</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Photo Upload Button */}
            <div>
              <Label htmlFor="photo-upload" className="cursor-pointer">
                <div className="border-2 border-dashed border-orange-400 rounded-lg p-6 text-center hover:bg-orange-100 transition-colors">
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={formData.event_photos.length >= 4 || uploadingPhoto}
                  />
                  {uploadingPhoto ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-orange-600 mr-2" />
                      <span className="text-orange-900 font-semibold">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Plus className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                      <p className="text-orange-900 font-semibold">
                        Click to upload photos
                      </p>
                      <p className="text-xs text-orange-700 mt-1 font-medium">
                        {formData.event_photos.length}/4 photos uploaded
                      </p>
                    </>
                  )}
                </div>
              </Label>
            </div>

            {/* Photo Preview Grid */}
            {formData.event_photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.event_photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Event photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-orange-400 shadow-md"
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Co-host Management (Optional) */}
        <Card className="glass-card mb-6 bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-300 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl">
              <Users className="inline h-6 w-6 mr-2" />
              Add Co-hosts (Optional)
            </CardTitle>
            <p className="text-sm text-white text-opacity-90">Add up to 5 co-hosts to help manage your event</p>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            {/* Current Co-hosts */}
            {coHosts.length > 0 && (
              <div>
                <h4 className="font-semibold text-indigo-900 mb-2">Co-hosts Added ({coHosts.length}/5):</h4>
                <div className="space-y-2">
                  {coHosts.map((coHost, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3 bg-white rounded-lg border-2 border-indigo-300 shadow-md"
                    >
                      <div>
                        <p className="font-semibold text-indigo-900">{coHost.name || coHost.email}</p>
                        <p className="text-sm text-gray-600">{coHost.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCoHosts(coHosts.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Co-host Form */}
            {coHosts.length < 5 && (
              <div className="border-t border-indigo-200 pt-4">
                <h4 className="font-semibold text-indigo-900 mb-3">Add Co-host</h4>
                
                {/* Method Selection */}
                <div className="flex gap-2 mb-4">
                  <Button
                    type="button"
                    variant={coHostMethod === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCoHostMethod('email')}
                    className={coHostMethod === 'email' ? 'bg-indigo-600' : ''}
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={coHostMethod === 'search' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCoHostMethod('search')}
                    className={coHostMethod === 'search' ? 'bg-indigo-600' : ''}
                  >
                    <Search className="h-4 w-4 mr-1" />
                    Search
                  </Button>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-indigo-900 font-semibold">Co-host Email</Label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={coHostEmail}
                      onChange={(e) => setCoHostEmail(e.target.value)}
                    />
                  </div>
                  {coHostMethod === 'email' && (
                    <div>
                      <Label className="text-indigo-900 font-semibold">Co-host Name (Optional)</Label>
                      <Input
                        placeholder="Enter name"
                        value={coHostName}
                        onChange={(e) => setCoHostName(e.target.value)}
                      />
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={() => {
                      if (!coHostEmail) {
                        toast.error('Please enter an email address');
                        return;
                      }
                      if (coHosts.some(ch => ch.email === coHostEmail)) {
                        toast.error('This email is already added');
                        return;
                      }
                      setCoHosts([...coHosts, { email: coHostEmail, name: coHostName || coHostEmail, method: coHostMethod }]);
                      setCoHostEmail('');
                      setCoHostName('');
                      toast.success('Co-host added to list');
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add to List
                  </Button>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900 font-semibold mb-1">
                    {coHostMethod === 'email' && '📧 Email Method'}
                    {coHostMethod === 'search' && '🔍 Search Method'}
                  </p>
                  <p className="text-xs text-blue-800">
                    {coHostMethod === 'email' && 'Add co-host by email. They will receive an invitation after event creation.'}
                    {coHostMethod === 'search' && 'Search for existing Contribo users by email.'}
                  </p>
                </div>
              </div>
            )}

            {/* Co-host Permissions Info */}
            <div className="border-t border-indigo-200 pt-4">
              <h4 className="text-sm font-semibold text-indigo-900 mb-2">Co-host Permissions:</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>✅ Approve/reject contributions</li>
                <li>✅ Share event & download tracker</li>
                <li>⚠️ Edit/Delete event (requires your approval)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={savingDraft || loading}
            className="bg-purple-600 text-white hover:bg-purple-700 border-none"
            data-testid="save-draft-btn"
          >
            {savingDraft ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Draft'
            )}
          </Button>
          <Button
            className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white px-8"
            onClick={handleSubmit}
            disabled={loading || savingDraft}
            data-testid="create-event-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Event'
            )}
          </Button>
        </div>
      </div>

      {/* Wishlist Modal */}
      {showWishlistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-t-xl">
              <h2 className="text-lg sm:text-2xl font-bold">Link from Wishlist</h2>
              <p className="text-xs sm:text-sm text-white text-opacity-90 mt-1">
                Select an existing item or add a new one
              </p>
            </div>

            <div className="p-4">
              {/* Wishlist Selector */}
              {wishlists.length > 0 && (
                <div className="mb-4">
                  <Label className="text-sm font-semibold text-gray-700">Select Wishlist</Label>
                  <select
                    value={selectedWishlistId || ''}
                    onChange={(e) => setSelectedWishlistId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-1"
                  >
                    {wishlists.map((wishlist) => (
                      <option key={wishlist.id} value={wishlist.id}>
                        {wishlist.name} ({wishlist.items?.length || 0} items)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* No Wishlists Message */}
              {wishlists.length === 0 && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-yellow-700 text-sm mb-2">You don&apos;t have any wishlists yet.</p>
                  <Button
                    type="button"
                    onClick={() => navigate('/wishlist/my-wishlists')}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm"
                  >
                    Create a Wishlist
                  </Button>
                </div>
              )}

              {/* Add New Item Button */}
              {wishlists.length > 0 && !showAddWishlistItem && (
                <Button
                  type="button"
                  onClick={() => setShowAddWishlistItem(true)}
                  className="w-full mb-4 bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Item to Wishlist
                </Button>
              )}

              {/* Add New Item Form */}
              {showAddWishlistItem && (
                <div className="mb-6 p-4 border-2 border-green-300 rounded-lg bg-green-50">
                  <h3 className="font-semibold text-lg mb-3 text-gray-800">Add New Item</h3>
                  <div className="space-y-3">
                    <div>
                      <Label>Category</Label>
                      <select
                        value={newWishlistItem.category_id || ''}
                        onChange={(e) => setNewWishlistItem({ ...newWishlistItem, category_id: e.target.value || null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Uncategorized</option>
                        {wishlistCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Item Name *</Label>
                      <Input
                        value={newWishlistItem.name}
                        onChange={(e) => setNewWishlistItem({ ...newWishlistItem, name: e.target.value })}
                        placeholder="e.g., iPhone 15 Pro"
                      />
                    </div>
                    <div>
                      <Label>Price (₹)</Label>
                      <Input
                        type="number"
                        value={newWishlistItem.price}
                        onChange={(e) => setNewWishlistItem({ ...newWishlistItem, price: e.target.value })}
                        placeholder="e.g., 129900"
                      />
                    </div>
                    <div>
                      <Label>Product Link (URL)</Label>
                      <Input
                        type="url"
                        value={newWishlistItem.link}
                        onChange={(e) => setNewWishlistItem({ ...newWishlistItem, link: e.target.value })}
                        placeholder="https://amazon.in/..."
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newWishlistItem.description}
                        onChange={(e) => setNewWishlistItem({ ...newWishlistItem, description: e.target.value })}
                        placeholder="Add any notes..."
                        rows="2"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setShowAddWishlistItem(false);
                          setNewWishlistItem({ name: '', category_id: null, price: '', link: '', description: '' });
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handleAddWishlistItem}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        Add Item
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Wishlist Items */}
              {wishlists.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-800">Wishlist Items</h3>
                  {getSelectedWishlistItems().length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No items in this wishlist. Add one above to get started!
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {getSelectedWishlistItems().map((item) => {
                        const isAlreadyLinked = contributionGoals.some(goal => goal.wishlist_item_id === item.id);
                        return (
                          <div
                            key={item.id}
                            className={`p-4 border-2 rounded-lg transition ${
                              isAlreadyLinked 
                                ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed' 
                                : 'border-gray-200 hover:border-purple-400 cursor-pointer bg-white'
                            }`}
                            onClick={() => !isAlreadyLinked && linkWishlistItem(item)}
                          >
                            <div className="flex items-start gap-3">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.name}
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                                  <LinkIcon className="text-gray-400" size={24} />
                                </div>
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800">{item.name}</h4>
                                {item.price && (
                                  <p className="text-sm text-gray-600">₹{item.price.toLocaleString()}</p>
                                )}
                                {item.description && (
                                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{item.description}</p>
                                )}
                                {isAlreadyLinked && (
                                  <p className="text-xs text-green-600 mt-1 font-semibold">✓ Already linked to a goal</p>
                                )}
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                disabled={isAlreadyLinked}
                                className={
                                  isAlreadyLinked 
                                    ? "bg-gray-400 text-white cursor-not-allowed" 
                                    : "bg-purple-600 hover:bg-purple-700 text-white"
                                }
                              >
                                {isAlreadyLinked ? 'Linked' : 'Select'}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Close Button */}
              <Button
                type="button"
                onClick={() => {
                  setShowWishlistModal(false);
                  setCurrentGoalIndex(null);
                  setShowAddWishlistItem(false);
                  setNewWishlistItem({ name: '', category_id: null, price: '', link: '', description: '' });
                }}
                variant="outline"
                className="w-full mt-4"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CreateEventPage;
